import dayjs, { ConfigType } from 'dayjs'
import { dateArrayType } from '../../constants/types'
import React from 'react'
import styled from '../../constants/theme'
import {
  BasicType,
  ElementState,
  ElementType,
  FormElementProps,
  QuestionType,
} from './FormElementParts'
import InputElement from './InputElement'
import { useI18n } from '../../../configuration/contexts/I18nContext'

export interface DateContainerProps {
  elementState?: ElementState
  type: BasicType | ElementType | QuestionType
}

const DateElement: React.FC<FormElementProps> = ({
  disabled,
  elementState,
  errorText,
  isRequired,
  label,
  name,
  onChange,
  type,
  value,
}) => {
  const i18n = useI18n()
  let theValue: ConfigType
  if (Array.isArray(value)) {
    // This is hacky as hell, but trying it to see if it'll fulfill dayjs's array contract
    const arrLength = Math.min(value.length, 7)
    theValue = Array<number>(7) as dateArrayType

    for (let i = 0; i < arrLength; i++) {
      theValue[i] = parseInt(value[i], 10)
    }
  } else {
    theValue = value
  }

  const dayjsDate = dayjs(theValue || undefined)
  const [date, setDate] = React.useState({
    month: value && dayjsDate.format('MM'),
    day: value && dayjsDate.format('DD'),
    year: value && dayjsDate.format('YYYY'),
  })

  const handleInput = (e: React.FormEvent<HTMLInputElement>) => {
    const maxLength = e.currentTarget.name === 'year' ? 4 : 2
    const newValue = e.currentTarget.value
      .replace(/[^0-9.]/g, '')
      .substring(0, maxLength)

    e.currentTarget.value = newValue
    setDate({ ...date, [e.currentTarget.name]: newValue })
  }

  React.useEffect(() => {
    if (
      date.month?.length === 2 &&
      date.day?.length === 2 &&
      date.year?.length === 4
    ) {
      onChange?.({ name, value: `${date.year}-${date.month}-${date.day}` })
    }
  }, [date, name, onChange])

  const requiredStr = isRequired ? ' *' : ''

  const dayText = i18n.t('dateParts.day')
  const monthText = i18n.t('dateParts.month')
  const yearText = i18n.t('dateParts.year')

  return (
    <DateGroup className={`DobElement ${type} ${elementState}`}>
      <div>
        <Label htmlFor="month">{`${monthText}${requiredStr}`}</Label>
        <InputElement
          aria-label={
            errorText ? errorText + `month ${label}` : `month ${label}`
          }
          disabled={disabled}
          id="month"
          label={monthText}
          name="month"
          onInput={handleInput}
          pattern="(0[1-9]|1[012])"
          placeholder="MM"
          isRequired={isRequired}
          type={ElementType.TEXT}
          value={date.month}
        />
      </div>
      <div>
        <Label htmlFor="day">{`${dayText}${requiredStr}`}</Label>
        <InputElement
          disabled={disabled}
          id="day"
          label={dayText}
          name="day"
          onInput={handleInput}
          pattern="(0[1-9]|1[0-9]|2[0-9]|3[01])"
          placeholder="DD"
          isRequired={isRequired}
          type={ElementType.TEXT}
          value={date.day}
        />
      </div>
      <div>
        <Label htmlFor="year">{`${yearText}${requiredStr}`}</Label>
        <InputElement
          disabled={disabled}
          id="year"
          label={yearText}
          name="year"
          onInput={handleInput}
          pattern="(1[89]|20)\d{2}"
          placeholder="YYYY"
          isRequired={isRequired}
          type={ElementType.TEXT}
          value={date.year}
        />
      </div>
    </DateGroup>
  )
}

export const DateGroup = styled.div({
  '& > div + div': {
    marginLeft: 10,
  },
  display: 'flex',
})

const Label = styled.label({
  fontSize: 10,
  fontWeight: 'bold',
})

export default DateElement
